// Status color mapping for markers
export const statusColors = {
  OPEN: '#3b82f6',      // blue-500
  FULL: '#a78bfa',      // purple-400
  STARTED: '#fde047',   // yellow-300
};

// Get current time in Brisbane
export function getBrisbaneNow() {
  const now = new Date();
  return new Date(now.toLocaleString('en-US', { timeZone: 'Australia/Brisbane' }));
}

// Get Brisbane event date from row (copied from EventTable)
export function getBrisbaneEventDate(row) {
  if (row.start && row.start !== 'N/A') {
    // row.start is UTC, convert to Brisbane
    return new Date(new Date(row.start).toLocaleString('en-US', { timeZone: 'Australia/Brisbane' }));
  }
  if (row.startDate && row.time) {
    // row.startDate is already Brisbane local date (YYYY-MM-DD), but time may be in 12hr format
    const [startTime] = row.time.split(' - ');
    const [hour, minuteRaw] = startTime.split(':');
    let minute = 0;
    let hourNum = parseInt(hour, 10);
    let ampm = startTime.toLowerCase().includes('pm') ? 'pm' : 'am';
    if (minuteRaw) {
      const [min, rest] = minuteRaw.split(' ');
      minute = parseInt(min, 10);
      if (rest && rest.toLowerCase() === 'pm' && hourNum < 12) hourNum += 12;
      if (rest && rest.toLowerCase() === 'am' && hourNum === 12) hourNum = 0;
    }
    if (!minuteRaw && ampm === 'pm' && hourNum < 12) hourNum += 12;
    if (!minuteRaw && ampm === 'am' && hourNum === 12) hourNum = 0;
    // Construct Brisbane local datetime
    const eventStartIso = `${row.startDate}T${hourNum.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:00+10:00`;
    return new Date(eventStartIso);
  }
  // Fallback: treat as Brisbane local midnight
  return new Date(row.startDate + 'T00:00:00+10:00');
}

// Format a date string as "Thu 1st May" in Brisbane time
export function formatDisplayDate(isoDate) {
  const date = new Date(isoDate + 'T00:00:00+10:00');
  const day = date.getDate();
  const daySuffix =
    day === 1 || day === 21 || day === 31 ? 'st' :
    day === 2 || day === 22 ? 'nd' :
    day === 3 || day === 23 ? 'rd' : 'th';
  const weekday = date.toLocaleDateString('en-AU', { timeZone: 'Australia/Brisbane', weekday: 'short' });
  const month = date.toLocaleDateString('en-AU', { timeZone: 'Australia/Brisbane', month: 'long' });
  return `${weekday} ${day}${daySuffix} ${month}`;
}

// Status calculation (match EventTable)
export function getEventStatus(row) {
  const now = getBrisbaneNow();
  const eventStart = getBrisbaneEventDate(row);
  let status = 'OPEN';
  const maxIsFinite = row.maxAttendees !== '∞' && row.maxAttendees !== 'unlimited';
  if (eventStart && now >= eventStart) {
    status = 'STARTED';
  } else if (
    (maxIsFinite && Number(row.registeredAttendees) >= Number(row.maxAttendees))
  ) {
    status = 'FULL';
  } else if (row.waitlistUserCount > 0) {
    status = 'FULL';
  }
  return status;
}

// Price formatting (match EventTable)
export function formatPrice(priceRange) {
  if (!priceRange || priceRange === 'N/A') return null;
  if (priceRange.includes('-')) {
    const [min, max] = priceRange.replace(/\$/g, '').split('-').map(s => s.trim());
    return min === max ? `$${min}` : `$${min}-$${max}`;
  }
  return priceRange.startsWith('$') ? priceRange : `$${priceRange}`;
}

// Duration formatting (match EventTable, only one pair of parentheses)
export function formatDuration(duration) {
  if (!duration || duration === 'N/A') return '';
  const clean = duration.replace(/^\(+|\)+$/g, '');
  return `(${clean})`;
}

// Stylish SVG marker icon
export function getStylishIcon(status) {
  const color = statusColors[status] || '#3b82f6';
  const svg = encodeURIComponent(`
    <svg width="36" height="48" viewBox="0 0 36 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <filter id="shadow" x="0" y="0" width="36" height="48">
        <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="#000" flood-opacity="0.15"/>
      </filter>
      <path d="M18 47C18 47 32 29.5 32 18C32 8.61116 25.3888 2 18 2C10.6112 2 4 8.61116 4 18C4 29.5 18 47 18 47Z" fill="#fff" filter="url(#shadow)" stroke="${color}" stroke-width="2"/>
      <circle cx="18" cy="18" r="9" fill="${color}" stroke="#fff" stroke-width="3"/>
    </svg>
  `);
  // Only import leaflet in the component, not here
  return {
    iconUrl: `data:image/svg+xml;utf8,${svg}`,
    iconSize: [36, 48],
    iconAnchor: [18, 47],
    popupAnchor: [0, -40],
    className: 'custom-leaflet-marker'
  };
}

// Get Google/Apple Maps URLs for a row
export function getMapUrls(row) {
  const [lat, lng] = row.coordinates.split(',').map(coord => coord.trim());
  return {
    google: `https://www.google.com/maps?q=${lat},${lng}`,
    apple: `http://maps.apple.com/?ll=${lat},${lng}&q=${encodeURIComponent(row.address)}`
  };
}
